import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Download, RefreshCw, Lock, Users } from "lucide-react";
import Seo from "@/components/seo/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gr_admin_token";

function Login({ onLogin }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/admin/login`, { email, password });
            localStorage.setItem(TOKEN_KEY, data.token);
            onLogin(data.token);
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Nie udało się zalogować.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="grid min-h-[70vh] place-items-center px-6">
            <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm rounded-3xl border border-border bg-card p-8">
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-gr-secondary text-gr-primary dark:bg-gr-dsecondary dark:text-gr-dprimary">
                    <Lock className="h-6 w-6" />
                </div>
                <h1 className="mt-5 text-2xl tracking-tight text-foreground">Panel Groszownika</h1>
                <p className="mt-1 text-sm text-muted-foreground">Zaloguj się, aby zobaczyć listę oczekujących.</p>
                <div className="mt-6 space-y-3">
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="e-mail administratora"
                        data-testid="admin-email"
                        className="w-full rounded-xl border border-border bg-background px-4 py-3 text-base text-foreground focus:border-gr-accent focus:outline-none"
                    />
                    <input
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="hasło"
                        data-testid="admin-password"
                        className="w-full rounded-xl border border-border bg-background px-4 py-3 text-base text-foreground focus:border-gr-accent focus:outline-none"
                    />
                </div>
                <button
                    type="submit"
                    disabled={loading}
                    data-testid="admin-login-submit"
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:brightness-110 active:scale-95 disabled:opacity-60"
                >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Zaloguj się"}
                </button>
            </form>
        </div>
    );
}

function Dashboard({ token, onLogout }) {
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/waitlist`, { headers: { Authorization: `Bearer ${token}` } });
            setItems(data.items || []);
            setCount(data.count || 0);
        } catch (err) {
            if (err.response?.status === 401) {
                toast.error("Sesja wygasła. Zaloguj się ponownie.");
                onLogout();
            } else {
                toast.error("Nie udało się pobrać danych.");
            }
        } finally {
            setLoading(false);
        }
    }, [token, onLogout]);

    useEffect(() => { load(); }, [load]);

    const exportCsv = async () => {
        setExporting(true);
        try {
            const res = await fetch(`${API}/admin/waitlist/export`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error("export failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "groszownik-waitlist.csv";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("Eksport nie powiódł się.");
        } finally {
            setExporting(false);
        }
    };

    const fmt = (iso) => {
        try { return new Date(iso).toLocaleString("pl-PL"); } catch (e) { return iso; }
    };

    return (
        <div className="container-gr py-12">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                    <h1 className="text-3xl tracking-tight text-foreground">Lista oczekujących</h1>
                    <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="h-4 w-4" /> <span data-testid="admin-count">{count}</span> zapisów
                    </p>
                </div>
                <div className="flex gap-2">
                    <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-secondary active:scale-95">
                        <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Odśwież
                    </button>
                    <button onClick={exportCsv} disabled={exporting || count === 0} data-testid="admin-export" className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:brightness-110 active:scale-95 disabled:opacity-50">
                        {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Eksport CSV
                    </button>
                    <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-secondary active:scale-95">
                        <LogOut className="h-4 w-4" /> Wyloguj
                    </button>
                </div>
            </div>

            <div className="mt-8 overflow-x-auto rounded-2xl border border-border">
                <table className="w-full min-w-[560px] text-sm" data-testid="admin-table">
                    <thead>
                        <tr className="border-b border-border bg-gr-surface2 dark:bg-gr-dsurface2">
                            <th className="px-4 py-3 text-left font-heading font-bold text-foreground">E-mail</th>
                            <th className="px-4 py-3 text-left font-heading font-bold text-foreground">Zgoda marketingowa</th>
                            <th className="px-4 py-3 text-left font-heading font-bold text-foreground">Źródło</th>
                            <th className="px-4 py-3 text-left font-heading font-bold text-foreground">Data</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">Ładowanie…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">Brak zapisów.</td></tr>
                        ) : (
                            items.map((it, i) => (
                                <tr key={i} className="border-b border-border last:border-0">
                                    <td className="px-4 py-3 font-medium text-foreground">{it.email}</td>
                                    <td className="px-4 py-3">{it.marketing_consent ? "Tak" : "Nie"}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{it.source}</td>
                                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmt(it.created_at)}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default function Admin() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
    }, []);

    return (
        <>
            <Seo title="Panel" path="/" noindex />
            {token ? <Dashboard token={token} onLogout={logout} /> : <Login onLogin={setToken} />}
        </>
    );
}
